import { z } from 'zod';
import { crowdexSubagentDataSchema } from './subagent-schema';

export { crowdexSubagentDataSchema } from './subagent-schema';

export const crowdexPlanEntrySchema = z.object({
  id: z.string().optional(),
  content: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
  status: z.enum(['pending', 'in_progress', 'completed']),
  dependencies: z.array(z.string()).optional(),
  files: z.array(z.string()).optional(),
});

export const crowdexPlanDataSchema = z.union([
  z.object({ entries: z.array(crowdexPlanEntrySchema) }),
  z.object({ markdown: z.string() }),
  z.object({ uri: z.string() }),
]);

export const crowdexUsageDataSchema = z.object({
  used: z.number(),
  size: z.number(),
  cost: z.object({ amount: z.number(), currency: z.string() }).nullable(),
});

export const crowdexDiffDataSchema = z.object({
  toolCallId: z.string(),
  path: z.string(),
  oldText: z.string().nullable(),
  newText: z.string(),
});

export const crowdexTerminalDataSchema = z.object({
  toolCallId: z.string(),
  terminalId: z.string(),
  output: z.string(),
  truncated: z.boolean(),
  exitCode: z.number().nullable(),
  signal: z.string().nullable(),
  /** The folder the command ran in, when its agent reports it. */
  cwd: z.string().optional(),
  /** How long the command ran, once it ended, when its agent reports or times it. */
  durationMs: z.number().optional(),
  /** Client-reconciled completion state for a detached background process. */
  backgroundCompleted: z.boolean().optional(),
});

export const crowdexCommandsDataSchema = z.object({
  commands: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
      inputHint: z.string().nullable(),
    }),
  ),
});

export const crowdexMcpAppDataSchema = z.object({
  toolCallId: z.string(),
  resourceUri: z.string(),
  html: z.string().nullable(),
  toolInput: z.json(),
  toolResult: z.json().nullable(),
});

export const crowdexSessionEventDataSchema = z.object({
  changes: z.array(
    z.object({
      field: z.enum([
        'agent',
        'model',
        'effort',
        'permissions',
        'plan',
        'collaboration',
        'environment',
        'repository',
        'branch',
      ]),
      from: z.string().nullable(),
      to: z.string(),
    }),
  ),
});

const crowdexElicitationPropertySchema = z.object({ type: z.string() }).catchall(z.unknown());

const crowdexElicitationOutcomeSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('accept'),
    content: z
      .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]))
      .optional(),
  }),
  z.object({ action: z.literal('decline') }),
  z.object({ action: z.literal('cancel') }),
]);

const crowdexElicitationRequestBaseSchema = z.object({
  requestId: z.string(),
  sessionId: z.string().nullable(),
  toolCallId: z.string().nullable(),
  message: z.string(),
});

const crowdexElicitationStateSchema = {
  status: z.enum(['pending', 'accepted', 'declined', 'cancelled']),
  outcome: crowdexElicitationOutcomeSchema.optional(),
} as const;

export const crowdexElicitationDataSchema = z.discriminatedUnion('mode', [
  crowdexElicitationRequestBaseSchema.extend({
    mode: z.literal('form'),
    requestedSchema: z.object({
      type: z.literal('object').optional(),
      title: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      properties: z.record(z.string(), crowdexElicitationPropertySchema).optional(),
      required: z.array(z.string()).nullable().optional(),
      _meta: z.record(z.string(), z.unknown()).nullable().optional(),
    }),
    ...crowdexElicitationStateSchema,
  }),
  crowdexElicitationRequestBaseSchema.extend({
    mode: z.literal('url'),
    elicitationId: z.string(),
    url: z.string(),
    ...crowdexElicitationStateSchema,
  }),
]);

const crowdexSessionConfigChoiceSchema = z.object({
  value: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
});

const crowdexSessionConfigOptionSchema = z.intersection(
  z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
  }),
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal('select'),
      currentValue: z.string(),
      options: z.union([
        z.array(crowdexSessionConfigChoiceSchema),
        z.array(
          z.object({
            group: z.string(),
            name: z.string(),
            options: z.array(crowdexSessionConfigChoiceSchema),
          }),
        ),
      ]),
    }),
    z.object({ type: z.literal('boolean'), currentValue: z.boolean() }),
  ]),
);

/** A message the user sent during the turn, at the moment the agent accepted it. */
export const crowdexSteerDataSchema = z.object({
  messageId: z.string().min(1).max(200),
  text: z.string().min(1).max(100_000),
});

/** Canonical schemas shared by AI SDK Chat stream validation and persistence. */
export const crowdexDataPartSchemas = {
  steer: crowdexSteerDataSchema,
  plan: crowdexPlanDataSchema,
  diff: crowdexDiffDataSchema,
  terminal: crowdexTerminalDataSchema,
  commands: crowdexCommandsDataSchema,
  usage: crowdexUsageDataSchema,
  subagent: crowdexSubagentDataSchema,
  mcpApp: crowdexMcpAppDataSchema,
  sessionEvent: crowdexSessionEventDataSchema,
  elicitation: crowdexElicitationDataSchema,
} as const;

/**
 * AI SDK 7's stream processor indexes schemas by the wire name (`data-plan`),
 * while historical-message validation indexes them by the logical name
 * (`plan`). Supplying both lets one canonical schema set cover both paths.
 */
export const crowdexChatDataPartSchemas = {
  ...crowdexDataPartSchemas,
  'data-plan': crowdexPlanDataSchema,
  'data-diff': crowdexDiffDataSchema,
  'data-terminal': crowdexTerminalDataSchema,
  'data-commands': crowdexCommandsDataSchema,
  'data-usage': crowdexUsageDataSchema,
  'data-subagent': crowdexSubagentDataSchema,
  'data-mcpApp': crowdexMcpAppDataSchema,
  'data-sessionEvent': crowdexSessionEventDataSchema,
  'data-elicitation': crowdexElicitationDataSchema,
  'data-steer': crowdexSteerDataSchema,
} as const;

/**
 * The workspace before and after an assistant turn, as trees of the host's checkpoint store,
 * with the files the turn changed (by any means: edit tools, shell commands, generators).
 * Another conversation's turn may run in the same folder meanwhile: what it changed is not this
 * turn's (`foreign`), and a file both may have changed is `shared`, never quietly claimed.
 */
export const crowdexTurnCheckpointSchema = z.object({
  before: z.string().regex(/^[0-9a-f]{40}([0-9a-f]{24})?$/),
  after: z.string().regex(/^[0-9a-f]{40}([0-9a-f]{24})?$/),
  files: z
    .array(
      z.object({
        path: z.string().min(1),
        status: z.enum(['added', 'modified', 'deleted']),
        additions: z.number().int().nonnegative(),
        deletions: z.number().int().nonnegative(),
        /** Another conversation's turn, running at the same time, may have changed it too. */
        shared: z.literal(true).optional(),
      }),
    )
    .max(500),
  /** More files changed than `files` lists. */
  truncated: z.literal(true).optional(),
  /**
   * Files the folder changed during the turn that another conversation's turn wrote: left out of
   * `files`, and never undone with this turn.
   */
  foreign: z.array(z.string().min(1)).max(10_000).optional(),
});

export const crowdexMessageMetadataSchema = z
  .object({
    sessionTitle: z.string().trim().min(1).max(200).optional(),
    modelId: z.string().trim().min(1).optional(),
    sessionConfigOptions: z.array(crowdexSessionConfigOptionSchema).optional(),
    stopReason: z
      .enum(['end_turn', 'max_tokens', 'max_turn_requests', 'refusal', 'cancelled'])
      .optional(),
    usage: crowdexUsageDataSchema.optional(),
    acpMessageId: z.string().optional(),
    /** Said in the live call: the user's words, or the call's voice answering them. */
    voice: z.boolean().optional(),
    checkpoint: crowdexTurnCheckpointSchema.optional(),
  })
  .catchall(z.unknown());

/**
 * UIMessage.metadata is optional on the wire, while persisted SQL rows may
 * contain null. Normalize both representations to the canonical metadata
 * object used internally before AI SDK validation continues.
 */
export const crowdexChatMessageMetadataSchema = crowdexMessageMetadataSchema
  .nullish()
  .transform((metadata) => metadata ?? {});
